import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlarmClock, Check, MoonStar, X, Clock, Link2, Repeat, Lock, ChevronLeft, ChevronRight, User, UserPlus, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CompleteReminderDialog from "@/components/reminders/CompleteReminderDialog";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

/**
 * Iter 219 — Global Reminder Popup.
 *
 * Renders a modal the instant a reminder's remind_at hits. Missed
 * reminders (fired while the user was offline) surface on next login /
 * next screen change via the initial `/pending-popups` fetch.
 *
 * Sources:
 *  • On mount → GET /workspaces/{ws}/reminders/pending-popups
 *  • WebSocket `reminder.popup` push → append to queue (from nag sweep)
 *  • 60s poll as safety-net when WS drops
 *
 * A popup is closed by acting on it (Complete / Snooze) OR by explicit
 * dismiss ("Later"). Dismissing acks the popup so the same instance
 * doesn't re-pop for the same user. Snooze / status change on the
 * reminder clears the ack server-side so the next fire wakes it again.
 */

const PRIORITY_ACCENT = {
  low: { border: "border-slate-400", strip: "bg-slate-400", chip: "bg-slate-100 text-slate-700 border-slate-300" },
  normal: { border: "border-sky-500", strip: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-300" },
  high: { border: "border-amber-500", strip: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-300" },
  urgent: { border: "border-rose-500", strip: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-300" },
};

export default function ReminderPopup() {
  const { activeWorkspaceId, user } = useAuth();
  const [queue, setQueue] = useState([]); // list of reminder objects
  const [idx, setIdx] = useState(0);
  const [members, setMembers] = useState([]);
  const [delegatePickerOpen, setDelegatePickerOpen] = useState(false);
  const seenIdsRef = useRef(new Set());
  const shownBeaconRef = useRef(new Set());
  const pollRef = useRef(null);
  const wsRef = useRef(null);

  const enqueue = useCallback((rs) => {
    if (!rs || !rs.length) return;
    setQueue((prev) => {
      const seen = new Set(prev.map((r) => r.id));
      const add = rs.filter((r) => r && r.id && !seen.has(r.id));
      add.forEach((r) => seenIdsRef.current.add(r.id));
      return prev.concat(add);
    });
  }, []);

  const fetchPending = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/reminders/pending-popups`);
      enqueue(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [activeWorkspaceId, enqueue]);

  const fetchOne = useCallback(async (rid) => {
    try {
      const { data } = await api.get(`/reminders/${rid}`);
      if (data && data.status === "open") enqueue([data]);
    } catch { /* silent */ }
  }, [enqueue]);

  // Initial fetch + poll every 60s as WS-drop safety net.
  // Feb 2026 R281 · Visibility-gated so background tabs stop hitting.
  useEffect(() => {
    if (!activeWorkspaceId || !user) return;
    fetchPending();
    return pollWhenVisible(fetchPending, 60_000);
  }, [activeWorkspaceId, user, fetchPending]);

  // Lazy-load members for the quick-delegate picker on first popup.
  // CRITICAL: reset members whenever activeWorkspaceId changes — the
  // previous fetch's list was leaking into a newly-switched workspace's
  // delegate dropdown.
  useEffect(() => { setMembers([]); }, [activeWorkspaceId]);
  useEffect(() => {
    if (!activeWorkspaceId || members.length) return;
    if (queue.length === 0) return;
    let cancelled = false;
    const wsAtRequest = activeWorkspaceId;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then(({ data }) => {
        // Guard: only accept the payload if we're still on the same
        // workspace (fast switching mid-fetch).
        if (cancelled || wsAtRequest !== activeWorkspaceId) return;
        setMembers(Array.isArray(data) ? data : (data.members || []));
      })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [activeWorkspaceId, members.length, queue.length]);

  // WebSocket listener for instant push
  useEffect(() => {
    if (!activeWorkspaceId || !user) return;
    const token = localStorage.getItem("co_token");
    if (!token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const base = backendUrl.replace(/^http/, "ws");
    const url = `${base}/api/v1/ws?token=${encodeURIComponent(token)}&workspace_id=${activeWorkspaceId}`;
    let ws;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === "reminder.popup" && msg.reminder_id) {
            fetchOne(msg.reminder_id);
          }
        } catch { /* ignore */ }
      };
    } catch { /* silently fall back to poll */ }
    return () => { try { ws && ws.close(); } catch { /* ignore */ } };
  }, [activeWorkspaceId, user, fetchOne]);

  // Reset index when queue shrinks past current idx
  useEffect(() => {
    if (idx >= queue.length && queue.length > 0) setIdx(0);
  }, [queue, idx]);

  const current = queue[idx];

  // R268~3 · Feb 2026 — Complete now opens CompleteReminderDialog to
  // capture a mandatory response note before hitting the endpoint.
  const [showCompleteDlg, setShowCompleteDlg] = useState(false);

  // Iter 226 · Beacon: log "shown" event exactly once per reminder per session
  useEffect(() => {
    if (!current) return;
    if (shownBeaconRef.current.has(current.id)) return;
    shownBeaconRef.current.add(current.id);
    api.post(`/reminders/${current.id}/events`, { event_type: "shown" }).catch(() => {});
  }, [current]);

  if (!current) return null;

  const removeCurrent = () => {
    setQueue((prev) => prev.filter((r) => r.id !== current.id));
    setIdx((i) => Math.max(0, Math.min(i, queue.length - 2)));
  };

  const doComplete = () => setShowCompleteDlg(true);

  const doSnooze = async (mins) => {
    const until = new Date(Date.now() + mins * 60 * 1000).toISOString();
    try {
      await api.post(`/reminders/${current.id}/snooze`, { snooze_until: until });
      toast.success(`Snoozed for ${mins < 60 ? mins + "m" : mins < 60 * 24 ? Math.round(mins / 60) + "h" : Math.round(mins / 60 / 24) + "d"}`);
      try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
      removeCurrent();
    } catch { toast.error("Failed to snooze"); }
  };

  const doDismiss = async () => {
    // "Later" — just ack this popup for this user (server-side)
    try {
      await api.post(`/reminders/${current.id}/ack-popup`);
    } catch { /* ignore, still remove locally */ }
    removeCurrent();
  };

  const accent = PRIORITY_ACCENT[current.priority] || PRIORITY_ACCENT.normal;
  const isDelegatee = current.delegated_to === user?.id && current.owner_id !== user?.id;
  const total = queue.length;

  // Iter 226 · "For whom" summary — who is this reminder for + what scope
  const forWhomLabel = (() => {
    if (current.delegated_to && current.delegated_to_name) {
      return isDelegatee
        ? `For you (delegated by ${current.owner_name || "teammate"})`
        : `For ${current.delegated_to_name}`;
    }
    if (current.owner_id === user?.id) return "For you";
    return `For ${current.owner_name || "the owner"}`;
  })();
  const scopeLabel = current.linked_entity
    ? `${current.linked_entity.kind}: ${current.linked_entity.name}`
    : null;

  const delegateTo = async (toUserId) => {
    try {
      await api.post(`/reminders/${current.id}/delegate`, { to_user_id: toUserId, notify: true });
      toast.success("Reassigned");
      setDelegatePickerOpen(false);
      removeCurrent();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to reassign"));
    }
  };

  const delegateOptions = members
    .filter((m) => (m.user_id || m.id) !== user?.id)
    .map((m) => ({
      id: m.user_id || m.id,
      label: (m.user?.name || m.name || m.user?.email || m.email || "").trim(),
    }))
    .filter((m) => m.label)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="reminder-popup-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full max-w-md bg-background border-l-4 ${accent.border} border-t border-r border-b border-border`}
        style={{ boxShadow: "0 30px 60px -12px rgba(0,0,0,0.55), 0 8px 20px -4px rgba(0,0,0,0.25)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/40">
          <div className={`w-6 h-6 flex items-center justify-center ${accent.strip} text-white`}>
            <AlarmClock className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              {isDelegatee ? "Reminder from " + (current.owner_name || "teammate") : "Reminder"}
            </div>
            <div className="text-xs font-semibold">
              It&apos;s time.
              {current.remind_at && (
                <span className="ml-1 text-muted-foreground font-normal">· {fmtRelative(current.remind_at)}</span>
              )}
            </div>
          </div>
          {total > 1 && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="reminder-popup-nav">
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className="p-0.5 disabled:opacity-30 hover:text-foreground"
                aria-label="Previous"
                data-testid="reminder-popup-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span>{idx + 1} / {total}</span>
              <button
                type="button"
                onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                disabled={idx >= total - 1}
                className="p-0.5 disabled:opacity-30 hover:text-foreground"
                aria-label="Next"
                data-testid="reminder-popup-next"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.post(`/workspaces/${activeWorkspaceId}/reminders/dismiss-all-popups`);
                toast.success(`Dismissed ${data?.acknowledged ?? 0} popup${data?.acknowledged === 1 ? "" : "s"}`);
                setQueue([]);
              } catch (e) { toast.error("Couldn't dismiss all popups"); }
            }}
            className="px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-rose-600 border border-border hover:border-rose-400 mr-1"
            title="Acknowledge every pending reminder popup for this workspace"
            data-testid="reminder-popup-dismiss-all"
          >
            Dismiss all
          </button>
          <button
            type="button"
            onClick={doDismiss}
            className="p-1 text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
            data-testid="reminder-popup-dismiss-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-start gap-2">
              <h2 className="text-lg font-bold flex-1 leading-tight" data-testid="reminder-popup-title">
                {current.title}
              </h2>
              {current.is_private && (
                <Lock className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" title="Private" />
              )}
            </div>
            {/* Iter 226 · For-Whom line */}
            <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground flex-wrap" data-testid="reminder-popup-for-whom">
              <User className="w-3 h-3 shrink-0" />
              <span className="font-medium text-foreground">{forWhomLabel}</span>
              {scopeLabel && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span>{scopeLabel}</span>
                </>
              )}
            </div>
            {current.note && (
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{current.note}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className={`px-1.5 py-0.5 border ${accent.chip} font-medium`}>
              {current.priority || "normal"}
            </span>
            {current.recurrence && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted text-muted-foreground border border-border">
                <Repeat className="w-3 h-3" /> {current.recurrence}
              </span>
            )}
            {current.linked_entity && current.linked_entity.href && (
              <Link
                to={current.linked_entity.href}
                onClick={doDismiss}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20"
                data-testid="reminder-popup-linked"
              >
                <Link2 className="w-3 h-3" />
                <span>Open {current.linked_entity.kind}: {current.linked_entity.name}</span>
              </Link>
            )}
            {current.delegated_to && !isDelegatee && current.delegated_to_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-300">
                delegated to {current.delegated_to_name}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 py-3 border-t border-border bg-muted/20 space-y-2">
          <button
            type="button"
            onClick={doComplete}
            className="w-full text-sm px-3 py-2 bg-primary text-primary-foreground hover:opacity-90 font-medium flex items-center justify-center gap-1.5"
            data-testid="reminder-popup-complete"
          >
            <Check className="w-4 h-4" /> Mark done
          </button>
          <div className="grid grid-cols-3 gap-1.5">
            <SnoozeBtn label="10 min" onClick={() => doSnooze(10)} testid="reminder-popup-snooze-10m" />
            <SnoozeBtn label="1 hour" onClick={() => doSnooze(60)} testid="reminder-popup-snooze-1h" />
            <SnoozeBtn label="Tomorrow" onClick={() => doSnooze(60 * 24)} testid="reminder-popup-snooze-1d" />
          </div>

          {/* Iter 226 · Quick delegate / reassign */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setDelegatePickerOpen((v) => !v)}
              className="w-full text-xs px-3 py-2 border border-border hover:bg-muted flex items-center justify-center gap-1.5 transition"
              data-testid="reminder-popup-delegate-toggle"
            >
              <UserPlus className="w-3.5 h-3.5" /> {current.delegated_to ? "Reassign to someone else" : "Delegate this"}
              <ChevronDown className={`w-3 h-3 transition ${delegatePickerOpen ? "rotate-180" : ""}`} />
            </button>
            {delegatePickerOpen && (
              <div className="absolute left-0 right-0 mt-1 border border-border bg-background max-h-56 overflow-y-auto z-20 shadow-xl" data-testid="reminder-popup-delegate-picker">
                {delegateOptions.length === 0 && (
                  <div className="p-3 text-xs text-muted-foreground text-center">No teammates found.</div>
                )}
                {delegateOptions.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => delegateTo(m.id)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2 border-b border-border/50 last:border-b-0"
                    data-testid={`reminder-popup-delegate-to-${m.id}`}
                  >
                    <User className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="truncate">{m.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={doDismiss}
            className="w-full text-xs px-3 py-1.5 text-muted-foreground hover:text-foreground"
            data-testid="reminder-popup-dismiss"
          >
            Later — remind me again in 15 min
          </button>
        </div>
      </div>
      {showCompleteDlg && (
        <CompleteReminderDialog
          reminder={current}
          onClose={() => setShowCompleteDlg(false)}
          onDone={() => {
            setShowCompleteDlg(false);
            try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
            removeCurrent();
          }}
        />
      )}
    </div>
  );
}

function SnoozeBtn({ label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-2 py-1.5 border border-border hover:bg-muted flex items-center justify-center gap-1 transition"
      data-testid={testid}
    >
      <MoonStar className="w-3 h-3" /> {label}
    </button>
  );
}

// Clock import kept in case future variant uses it — no-op export for tree-shaking
export { Clock };
